import React from 'react';
// Contexts
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faScrewdriver } from '@fortawesome/free-solid-svg-icons';
import { LinearProgress } from '@mui/material';
import { withRouter } from 'react-router-dom';
import { Row, Col } from 'reactstrap';
import { Can } from '../Can';
import CommonContext from '../Common';
import { ApiRoutes } from '../ApiRoutes';
import { StatusForm } from '../common/Generic Status/StatusForm';
import { ApplicationPaths } from '../api-authorization/ApiAuthorizationConstants';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    indexCellRenderer,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import {
    FlexRow,
    FlexStartRow,
    FlexEndRow,
    PageHeading,
    SmallButton,
    PageWrap,
} from '../common/forms/FormElements';
import { util } from '../Util';
import { EmployeeAccountStatus } from './EmployeeAccountStatus';

class EmployeeAccountStatusIndex extends React.Component {
    static contextType = CommonContext;

    // #region [ Constructor and Overrides ]

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            loading: true,
            employeeAccountStatus: null,
            readOnly: true,
            formValidated: false,
            errors: {},
            showEmployeeAccountStatusForm: false,
            selectedEmployeeAccountStatus: null,
            rowData: {},
        };
        this.onSubmit = this.onSubmit.bind(this);
        this.onSearch = this.onSearch.bind(this);
        this.onAddEmployeeAccountStatus =
            this.onAddEmployeeAccountStatus.bind(this);
        this.onEditEmployeeAccountStatus =
            this.onEditEmployeeAccountStatus.bind(this);
        this.onSaveEmployeeAccountStatus =
            this.onSaveEmployeeAccountStatus.bind(this);
        this.onEmployeeAccountStatusSelectionChanged =
            this.onEmployeeAccountStatusSelectionChanged.bind(this);
        this.onGridReady = this.onGridReady.bind(this);
    }

    componentDidMount() {
        this.populateState();
    }

    componentWillUnmount() {}

    // #region [ Status Flyout for Add/Edit ]

    // Add New Status
    onAddEmployeeAccountStatus() {
        const { employeeAccountStatus } = this.state;
        const new_status = new EmployeeAccountStatus();
        // This is a new status, embed the Id and description.
        new_status.id = employeeAccountStatus.id ?? 0;
        new_status.description = employeeAccountStatus.description;
        this.setState({
            selectedEmployeeAccountStatus: new_status,
            showEmployeeAccountStatusForm: true,
        });
    }

    onClearErrorNotificationClicked = (e) => {
        e.stopPropagation();
        this.setState((state) => ({ errors: {} }));
    };

    // Close status window
    onCloseEmployeeAccountStatus(response) {
        this.resetForm();
        this.props.onClose(response);
    }

    onDataFailure() {
        alert('Unable to fetch table data.');
    }

    onDataSuccess(rows, lastRowIndex) {
        this.setState({ rowData: rows });
    }

    // Delete status shown
    onDeleteEmployeeAccountStatus = async (e) => {
        const { employeeAccountStatus } = this.state;
        const response = await util.fetch
            .delete(
                ApiRoutes.employeeAccountStatus.delete(employeeAccountStatus.id)
            )
            .catch(this.onCatchFetchError);
        if (response) this.onClose(response);
    };

    // Status table edit click handler
    onEditEmployeeAccountStatus(status) {
        this.setState({
            selectedEmployeeAccountStatus: JSON.parse(JSON.stringify(status)),
            showEmployeeAccountStatusForm: true,
        });
    }

    onEmployeeAccountStatusSelectionChanged = (event) => {
        const selection = event.api.getSelectedRows();
        this.setState({
            selectedEmployeeAccountStatus: selection.length
                ? selection[0]
                : null,
        });
    };

    // #endregion

    // #region [ Grid ]

    onGridReady(params) {
        const url = ApiRoutes.employeeAccountStatus.search();
        params.api.setDatasource(createDataSource(url, this.state.gridOptions));
    }

    onInputChange = (event) => {
        const { employeeAccountStatus } = this.state;
        employeeAccountStatus.description = event.target.value;

        this.setState({
            employeeAccountStatus,
        });
    };

    // After status save callback
    onSaveEmployeeAccountStatus(status) {
        if (status) {
            this.setState({ showEmployeeAccountStatusForm: false });
            // refresh the grid
        }
    }

    async onSearch() {
        alert('search');
    }

    // #endregion

    async onSubmit() {
        // Clear any fluent api errors
        this.setState((state) => ({ errors: {} }));

        const { employeeAccountStatus } = this.state;

        const url = employeeAccountStatus.id
            ? ApiRoutes.employeeAccountStatus.update(employeeAccountStatus.id)
            : ApiRoutes.employeeAccountStatus.create();
        const fetch_stat = employeeAccountStatus.id
            ? util.fetch.put
            : util.fetch.post;

        const response = await fetch_stat(url, employeeAccountStatus).catch(
            (err) => {
                if (
                    err &&
                    err.validationMessages &&
                    (Object.keys(err.validationMessages) ?? []).length
                )
                    this.setState((state) => ({
                        errors: err.validationMessages,
                    }));
            }
        );

        if (response.ok) {
            const id = await response.json();

            // if we got this far, redirect to employee edit page.
            this.redirect(id);
        }
    }

    async populateState() {
        const employeeAccountStatusId = this.props.match.params.id;

        const data = await Promise.all([
            employeeAccountStatusId
                ? util.fetch.js(
                      ApiRoutes.employeeAccountStatus.byId(
                          employeeAccountStatusId
                      )
                  )
                : new EmployeeAccountStatus(),
        ]);

        const gridOptions = createGridOptions();

        gridOptions.components = {
            statusFilter: DataGridSelectFilter,
            equipmentTypeFilter: DataGridSelectFilter,
        };

        gridOptions.columnDefs = [
            {
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'EmployeeAccountStatus.Description',
                sortable: true,
                headerName: 'Description',
                field: 'description',
                sort: { direction: 'asc', priority: 0 },
            },
        ];

        gridOptions.onSelectionChanged =
            this.onEmployeeAccountStatusSelectionChanged;

        gridOptions.onGridReady = this.onGridReady;

        this.setState((state) => ({
            loading: false,
            employeeAccountStatus: data[0],
            readOnly: false,
            gridOptions,
        }));
    }

    // #endregion

    redirect(id) {
        this.props.history.push(
            `${ApplicationPaths.employeeAccountStatus}/${id}`
        );
    }

    // #region [Render and Render Functions]

    render() {
        if (this.state.loading)
            return <LinearProgress variant="indeterminate" color="secondary" />;

        const { rowData, gridOptions } = this.state;

        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faScrewdriver}
                        className="mr-2 text-muted"
                    />
                    <span>Employee Account Status</span>
                </PageHeading>
                <Row>
                    <Col>
                        <FlexRow>
                            <FlexStartRow />
                            <FlexEndRow>
                                <Can I="create,view,edit" a="equipment">
                                    <CommonContext.Consumer>
                                        {(value) => (
                                            <SmallButton
                                                type="button"
                                                disabled={!!value.formIsOpen}
                                                hidden={
                                                    !this.state
                                                        .selectedEmployeeAccountStatus
                                                }
                                                onClick={
                                                    this
                                                        .onEditEmployeeAccountStatus
                                                }
                                            >
                                                <i className="fa fa-edit fa-md mr-2" />
                                                View Selected
                                            </SmallButton>
                                        )}
                                    </CommonContext.Consumer>
                                </Can>
                                <Can I="create" a="equipment">
                                    <CommonContext.Consumer>
                                        {(value) => (
                                            <SmallButton
                                                type="button"
                                                disabled={!!value.formIsOpen}
                                                onClick={
                                                    this
                                                        .onAddEmployeeAccountStatus
                                                }
                                            >
                                                <i className="fa fa-plus-circle fa-md mr-2" />
                                                Add Employee Account Status
                                            </SmallButton>
                                        )}
                                    </CommonContext.Consumer>
                                </Can>
                            </FlexEndRow>
                        </FlexRow>
                    </Col>
                </Row>
                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                />
                <StatusForm
                    status={this.state.selectedEmployeeAccountStatus}
                    show={this.state.showEmployeeAccountStatusForm}
                    onClose={() =>
                        this.setState({ showEmployeeAccountStatusForm: false })
                    }
                    onSaveCallback={this.onSaveEmployeeAccountStatus}
                    statusName="Employee Account Status"
                    route="employeeAccountStatus"
                />
            </PageWrap>
        );
    }

    // #endregion
}
export default withRouter(EmployeeAccountStatusIndex);
